import styled from '@emotion/styled';
import {
  EntityEventLabels,
  EntityEventState,
  EntityEventStateLabels,
  EntityEventType,
  type EntityEventTypeBasicAlert,
} from '@racemap/sdk/schema/entityEvents';
import { formatLabel } from '@racemap/utilities/formatting';
import type { RacemapStarter } from '@racemap/utilities/types/events';
import { Form, Select, type SelectProps } from 'antd';
import Fuse from 'fuse.js';
import type { DateTime } from 'luxon';
import type { FC } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useEventStarters } from 'src/lib/customHooks/useEventStarters';
import { DateRangePicker } from '../BasicComponents/DateRangePicker';
import { EntityEventsSelect } from '../BasicComponents/EntityEventsSelect/EntityEventsSelect';
import { basicAlertEventTypes } from './Alerts';

type AlertEventStateType =
  | EntityEventState.NEW
  | EntityEventState.IN_PROGRESS
  | EntityEventState.STANDBY
  | EntityEventState.RESOLVED;

export type AssignedToType = 'all' | 'none' | 'me' | 'others';

export interface AlertsFilterValues {
  'alert-event-type'?: Array<
    | EntityEventType.AWAY_FROM_TRACK
    | EntityEventType.LOW_BATTERY
    | EntityEventType.CRASH
    | EntityEventType.NO_MOVEMENT
    | EntityEventType.SOS
    | EntityEventType.POWER_OFF
  >;
  'date-range'?: [DateTime, DateTime];
  'starter-device-ids'?: Array<string>;
  'assigned-to'?: AssignedToType;
  'state'?: Array<AlertEventStateType>;
}

interface Props {
  onAlertEventTypeChange?: (value: Array<EntityEventTypeBasicAlert>) => void;
  onDateRangeChange?: (value: [DateTime, DateTime]) => void;
  onAssignedToChange?: (value: AssignedToType) => void;
  onStateFilterChange?: (
    value: Array<
      | EntityEventState.NEW
      | EntityEventState.IN_PROGRESS
      | EntityEventState.STANDBY
      | EntityEventState.RESOLVED
    >,
  ) => void;
  onStarterDeviceIdsChange?: (value: Array<string>) => void;
  dateRange?: [DateTime, DateTime];
  assignedTo?: string;
  stateFilter?: Array<AlertEventStateType>;
  minDate: DateTime;
  maxDate: DateTime;
  eventId?: string;
}

export const AlertsFilter: FC<Props> = ({
  onAlertEventTypeChange,
  onDateRangeChange,
  onAssignedToChange,
  onStateFilterChange,
  onStarterDeviceIdsChange,
  dateRange,
  assignedTo,
  stateFilter,
  minDate,
  maxDate,
  eventId,
}) => {
  const [form] = Form.useForm<AlertsFilterValues>();

  const { starters } = useEventStarters({ eventId });

  const startersWithDeviceId = useMemo(
    () => (starters ? starters.filter((s) => !!s.deviceId) : []),
    [starters],
  );

  const fuse = useMemo(() => {
    if (!startersWithDeviceId.length) return null;
    const fuseOptions = {
      keys: ['name', 'startNumber'],
      threshold: 0.3,
    };
    return new Fuse(startersWithDeviceId, fuseOptions);
  }, [startersWithDeviceId]);

  const [filteredStarters, setFilteredStarters] =
    useState<Array<RacemapStarter>>(startersWithDeviceId);

  useEffect(() => {
    setFilteredStarters(startersWithDeviceId);
  }, [startersWithDeviceId]);

  const formItemLayout = {
    labelCol: { span: 6 },
  };

  const options: SelectProps['options'] = [
    { label: EntityEventLabels.AWAY_FROM_TRACK, value: EntityEventType.AWAY_FROM_TRACK },
    { label: EntityEventLabels.LOW_BATTERY, value: EntityEventType.LOW_BATTERY },
    { label: EntityEventLabels.CRASH, value: EntityEventType.CRASH },
    { label: EntityEventLabels.NO_MOVEMENT, value: EntityEventType.NO_MOVEMENT },
    { label: EntityEventLabels.SOS, value: EntityEventType.SOS },
    { label: EntityEventLabels.POWER_OFF, value: EntityEventType.POWER_OFF },
  ];

  return (
    <StyledForm
      form={form}
      name="validate_other"
      initialValues={{
        'alert-event-type': options.map((option) => option.value),
        'date-range': dateRange,
        'starter-device-ids': [],
        'assigned-to': assignedTo,
        state: stateFilter,
      }}
      {...formItemLayout}
      onValuesChange={(changedValues, _allValues) => {
        if (changedValues['alert-event-type']) {
          onAlertEventTypeChange?.(changedValues['alert-event-type']);
        }
        if (changedValues['date-range']) {
          onDateRangeChange?.(changedValues['date-range']);
        }
        if (changedValues['assigned-to']) {
          onAssignedToChange?.(changedValues['assigned-to']);
        }
        if (changedValues.state) {
          onStateFilterChange?.(changedValues.state);
        }
        if (changedValues['starter-device-ids']) {
          onStarterDeviceIdsChange?.(changedValues['starter-device-ids']);
        }
      }}
    >
      <Form.Item name="starter-device-ids" label="Starter">
        <Select
          showSearch
          mode="multiple"
          placeholder="Select Starter"
          filterOption={false}
          onSearch={(value) => {
            if (!value.trim()) {
              setFilteredStarters(startersWithDeviceId);
            } else {
              const trimmedValue = value.trim();
              const exactMatches = startersWithDeviceId.filter(
                (starter) =>
                  starter.name === trimmedValue ||
                  starter.startNumber === trimmedValue ||
                  starter.id === trimmedValue,
              );

              if (exactMatches.length > 0) {
                setFilteredStarters(exactMatches);
              } else if (fuse) {
                const results = fuse.search(value);
                setFilteredStarters(results.map((result) => result.item));
              }
            }
          }}
          options={filteredStarters.map((starter: RacemapStarter) => ({
            label: formatLabel([starter.startNumber, starter.name]),
            value: starter.deviceId,
          }))}
        />
      </Form.Item>

      <Form.Item name="assigned-to" label="Assigned To">
        <Select
          placeholder="Select Assignment"
          options={[
            { label: 'All', value: 'all' },
            { label: 'None', value: 'none' },
            { label: 'Assigned to Me', value: 'me' },
            { label: 'Assigned to Others', value: 'others' },
          ]}
        />
      </Form.Item>

      <Form.Item name="state" label="State">
        <Select
          placeholder="Select State"
          mode="multiple"
          options={[
            { label: EntityEventStateLabels[EntityEventState.NEW], value: EntityEventState.NEW },
            {
              label: EntityEventStateLabels[EntityEventState.IN_PROGRESS],
              value: EntityEventState.IN_PROGRESS,
            },
            {
              label: EntityEventStateLabels[EntityEventState.STANDBY],
              value: EntityEventState.STANDBY,
            },
            {
              label: EntityEventStateLabels[EntityEventState.RESOLVED],
              value: EntityEventState.RESOLVED,
            },
          ]}
        />
      </Form.Item>

      <Form.Item name="alert-event-type" label="Event Type">
        <EntityEventsSelect
          value={form.getFieldValue('alert-event-type') || []}
          onChange={(value) => form.setFieldValue('alert-event-type', value)}
          possibleEventTypes={basicAlertEventTypes}
        />
      </Form.Item>
      <Form.Item name="date-range" label="Date Range">
        <DateRangePicker showTime minDate={minDate} maxDate={maxDate} />
      </Form.Item>
    </StyledForm>
  );
};

const StyledForm = styled(Form<AlertsFilterValues>)`
  width: 100%;
  display: flex;
  flex-direction: column;
  padding-right: 20px;
`;
